import { useEffect, useState } from 'react';
import Image, { type ImageProps } from 'next/image';

import { styled, type BoxProps } from '@/primitives';
import { useGetImage } from '@/hooks/useGetImage';

const EMPTY_SVG_DATA_URI =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';

const ImageWithSx = styled(Image)({});

function isBlob(value: unknown): value is Blob {
  return value instanceof Blob;
}

export interface DocumentMultiImageProps extends Omit<ImageProps, 'src'> {
  documentEntityId: string;
  fallbackImage?: string;
  sx?: BoxProps['sx'];
}

export function DocumentMultiImage({
  alt,
  documentEntityId,
  fallbackImage,
  ...props
}: DocumentMultiImageProps) {
  const [srcData, setSrcData] = useState<string>(
    () => fallbackImage || EMPTY_SVG_DATA_URI,
  );
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const { data: response } = useGetImage(documentEntityId);

  useEffect(() => {
    const data = response?.data;
    if (isBlob(data) && data.size > 0) {
      const objectUrl = URL.createObjectURL(data);
      setObjectUrl(objectUrl);
    } else {
      setObjectUrl(null);
    }
  }, [response]);

  if (objectUrl) {
    return (
      <ImageWithSx
        {...props}
        src={objectUrl}
        alt={'alt'}
        unoptimized
        style={{ borderRadius: '8px' }}
      />
    );
  } else {
    return (
      <ImageWithSx
        {...props}
        alt={alt}
        className="fallback"
        priority
        src={srcData}
        unoptimized
        style={{ borderRadius: '8px' }}
      />
    );
  }
}
